import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Heading } from './ui';
import { getUserPosts } from '../services/api';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const FriendHistoryModal = ({ isOpen, onClose, friend, onPostClick }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
            if (friend?.id) {
                fetchFriendPosts();
            }
        } else {
            document.body.classList.remove('modal-open');
        }
        
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen, friend]);
    
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);
    
    const fetchFriendPosts = async () => {
        if (!user || !friend?.id) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            const postsData = await getUserPosts(friend.id, { limit: 50 }, token);
            setPosts(postsData.posts || []);
        } catch (err) {
            console.error('Error fetching friend posts:', err);
            setError('Failed to load posts');
        } finally {
            setLoading(false);
        }
    };
    
    if (!isOpen || !friend) return null;
    
    const displayName = friend.display_name || friend.username || 'Unknown';
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    
    const getPostImage = (post) => {
        if (post.final_image?.url) return post.final_image.url;
        if (post.progress_photos && post.progress_photos.length > 0 && post.progress_photos[0]?.url) {
            return post.progress_photos[0].url;
        }
        if (post.recipe_images && Array.isArray(post.recipe_images) && post.recipe_images.length > 0) {
            const firstImage = post.recipe_images[0];
            if (typeof firstImage === 'string') return firstImage;
        }
        return null;
    };
    
    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={onClose}
        >
            <div 
                className="bg-neutral-0 hairline max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 hairline-b">
                    <div className="flex items-center gap-3">
                        <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center font-mono text-lg font-medium"
                            style={{ 
                                backgroundColor: colorTokens.neutral[900],
                                color: colorTokens.neutral[0]
                            }}
                        >
                            {displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <Heading level={3}>{displayName}'s Cooking Sessions</Heading>
                            <Text className="text-sm opacity-60">
                                {posts.length} {posts.length === 1 ? 'session' : 'sessions'} shared
                            </Text>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                    >
                        ✕
                    </button>
                </div>
                
                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="text-center py-12">
                            <Text className="opacity-60">Loading sessions...</Text>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <Text className="text-red-600">{error}</Text>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-12">
                            <Text className="opacity-60">No published sessions yet</Text>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {posts.map((post) => {
                                const postImage = getPostImage(post);
                                return (
                                    <button
                                        key={post.id}
                                        onClick={() => {
                                            onPostClick(post);
                                            onClose();
                                        }}
                                        className="bg-neutral-50 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all duration-120 group cursor-pointer text-left"
                                    >
                                        {/* Plate image */}
                                        <div className="aspect-square overflow-hidden">
                                            {postImage ? (
                                                <img 
                                                    src={postImage}
                                                    alt={post.recipe_title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div 
                                                    className="w-full h-full flex items-center justify-center"
                                                    style={{ backgroundColor: colorTokens.neutral[50] }}
                                                >
                                                    <Text className="font-mono text-xs opacity-60">No Image</Text>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Info */}
                                        <div className="p-3">
                                            <Text className="text-xs font-medium mb-1 line-clamp-2 group-hover:text-neutral-0">
                                                {post.recipe_title}
                                            </Text>
                                            <Text className="text-xs opacity-60 font-mono group-hover:text-neutral-0 group-hover:opacity-80">
                                                {formatDate(post.posted_at)}
                                            </Text>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FriendHistoryModal;

